import { forwardRef } from 'react';
import { cn } from '../utils/cn';
import { Breadcrumbs, type Crumb } from './Breadcrumbs';
import { Icon, resolveIconToken } from '../primitives/Icon';
import { SyntheticDataBadge } from './StatusBadges';
import { formatRelativeTime } from '../utils/format';

export interface PageHeaderProps extends React.HTMLAttributes<HTMLElement> {
  title: string;
  subtitle?: React.ReactNode;
  breadcrumbs?: Crumb[];
  /** Status chips: health, lifecycle, tier. Composed by the page. */
  badges?: React.ReactNode;
  actions?: React.ReactNode;
  /** Right-hand summary strip, as in the Product 360 reference header. */
  summary?: React.ReactNode;
  iconToken?: string | null;
  iconTone?: 'action' | 'attention' | 'teal' | 'navy' | 'success';
  refreshedAt?: string | null;
  isSyntheticData?: boolean;
}

const iconToneClasses = {
  action: 'bg-action text-action-contrast',
  attention: 'bg-attention text-attention-contrast',
  teal: 'bg-teal text-white',
  navy: 'bg-navy text-navy-contrast',
  success: 'bg-success text-success-contrast',
} as const;

export const PageHeader = forwardRef<HTMLElement, PageHeaderProps>(function PageHeader(
  {
    title,
    subtitle,
    breadcrumbs,
    badges,
    actions,
    summary,
    iconToken,
    iconTone = 'action',
    refreshedAt,
    isSyntheticData,
    className,
    ...rest
  },
  ref,
) {
  return (
    <header ref={ref} className={cn('flex flex-col gap-3', className)} {...rest}>
      {breadcrumbs?.length ? <Breadcrumbs items={breadcrumbs} /> : null}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          {iconToken ? (
            <span
              aria-hidden="true"
              className={cn(
                'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
                iconToneClasses[iconTone],
              )}
            >
              <Icon name={resolveIconToken(iconToken, 'cube')} size={22} />
            </span>
          ) : null}
          <div className="flex min-w-0 flex-col gap-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-title font-semibold text-content">{title}</h1>
              {badges}
              {isSyntheticData ? <SyntheticDataBadge /> : null}
            </div>
            {subtitle ? (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-dense-lg text-content-secondary">
                {subtitle}
              </div>
            ) : null}
          </div>
        </div>
        {actions ? (
          <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </div>
      {summary ? (
        <div className="flex flex-wrap items-stretch gap-x-6 gap-y-3 rounded-xl border border-line bg-surface px-4 py-3 shadow-card">
          {summary}
        </div>
      ) : null}
      {refreshedAt !== undefined ? (
        <p className="flex items-center gap-1.5 text-dense text-content-muted">
          <Icon name="clock" size={12} />
          Data refreshed {formatRelativeTime(refreshedAt ?? null)}
        </p>
      ) : null}
    </header>
  );
});

export interface PageHeaderStatProps {
  label: string;
  value: React.ReactNode;
  note?: React.ReactNode;
}

/** One cell of the header summary strip. */
export function PageHeaderStat({ label, value, note }: PageHeaderStatProps) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-dense text-content-muted">{label}</span>
      <span className="text-body-lg font-semibold tabular-nums text-content">{value}</span>
      {note ? <span className="text-dense text-content-secondary">{note}</span> : null}
    </div>
  );
}
